import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mountain,
  MapPin,
  TrendingUp,
  Clock,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Calendar,
  Users,
  Baby,
  Route,
  Phone,
  MessageCircle,
  Mail,
  Navigation as NavigationIcon,
} from "lucide-react";
import ClientLayout from "../../components/Layout/ClientLayout";
import NoImage from "../../assets/images/No_Image_Available.jpg";
import { GetTableListAPI } from "../../apiCaller/getAPIsCaller";
import {
  useGetAdventuresQuery,
  useGetDestinationsDetailQuery,
  useGetDestinationsImagesQuery,
} from "../../apiCaller/apis/getApi";
import HtmlContent from "../../utils/DisplayHTML";
import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-coverflow";

// import required modules
import { Autoplay, Navigation, EffectCoverflow, Pagination } from "swiper/modules";
import SpinningLoader from "../../components/SpinningLoader/SpinningLoader";

const DestinatioDetail = () => {
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState({});
  const [coverImage, setCoverImage] = useState();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const navigate = useNavigate();
  const { destinationId } = useParams();
  const {
    data: destination,
    error: destinationError,
    isLoading: destinationIsLoading,
    isFetching: destinationIsFetching,
  } = GetTableListAPI(
    { destinationId: destinationId },
    useGetDestinationsDetailQuery
  );

  const {
    data: destinationImages,
    error: destinationImagesError,
    isLoading: destinationImagesIsLoading,
    isFetching: destinationImagesIsFetching,
  } = GetTableListAPI(
    { destinationId: destinationId },
    useGetDestinationsImagesQuery
  );

  const {
    data: adventures,
    error: adventuresError,
    isLoading: adventuresIsLoading,
    isFetching: adventuresIsFetching,
  } = GetTableListAPI(search, useGetAdventuresQuery);

  useEffect(() => {
    if (adventures !== undefined && adventures?.results !== undefined) {
      setEvents(adventures?.results);
    }
  }, [adventures]);

  useEffect(() => {
    if (destination !== undefined) {
      const newSearch = { ...search, q: destination?.name };
      setSearch(newSearch);
    }
  }, [destination]);

  useEffect(() => {
    if (destinationImages !== undefined && destinationImages?.results?.length > 0) {
      setCoverImage(destinationImages?.results?.find((x) => x.is_display_image === true));
    }
  }, [destinationImages]);

  const handleClick = (image, index) => {
    setCoverImage(image);
    setActiveImageIndex(index);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner friendly":
        return "from-green-500 to-emerald-500";
      case "moderate":
        return "from-amber-500 to-orange-500";
      case "challenging":
        return "from-orange-500 to-red-500";
      case "extreme":
        return "from-red-600 to-rose-700";
      default:
        return "from-mamba-sky-blue to-mamba-navy";
    }
  };

  if (destinationIsLoading) {
    return (
      <ClientLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex p-6 bg-mamba-sky-blue/10 rounded-full mb-4">
              <Mountain className="w-16 h-16 text-mamba-sky-blue animate-pulse" />
            </div>
            <p className="text-mamba-navy text-lg font-montserrat">Loading destination details...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }
  return (
    <ClientLayout>
      {/* Hero Section */}
      <div className="relative h-[600px] lg:h-[700px] overflow-hidden">
        {/* Background Image with Animation */}
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0"
        >
          {destinationImagesIsFetching || destinationImagesIsLoading ? (
            <div className="h-full flex items-center justify-center bg-mamba-navy">
              <SpinningLoader isLoading={true} hasData={false} />
            </div>
          ) : (
            <img
              src={coverImage?.image_url || NoImage}
              alt={destination?.name}
              className="w-full h-full object-cover"
            />
          )}
        </motion.div>

        {/* Mamba Navy Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/90 via-slate-800/70 to-mamba-sky-blue/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/95 via-transparent to-transparent" />

        {/* Animated Background Elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-mamba-golden/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-20 w-40 h-40 bg-mamba-sky-blue/10 rounded-full blur-3xl animate-pulse" />

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl"
          >
            {/* Back Button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              onClick={() => navigate(-1)}
              className="absolute top-8 left-8 inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-montserrat font-semibold hover:bg-white/20 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </motion.button>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 mb-6 px-5 py-2 bg-mamba-golden/20 backdrop-blur-md border border-mamba-golden/40 rounded-full">
              <Sparkles className="w-5 h-5 text-mamba-golden" />
              <span className="text-sm text-white font-montserrat font-bold tracking-wider uppercase">
                Destination
              </span>
            </div>

            {/* Title */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black font-montserrat text-white mb-6 tracking-tight leading-tight">
              {destination?.name}
            </h1>

            {/* Quick Info Pills */}
            <div className="flex flex-wrap gap-3 justify-center mb-8">
              <div className="px-5 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm font-medium">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {destination?.location || destination?.country}
                </div>
              </div>
              <div
                className={`px-5 py-2 bg-gradient-to-r ${getDifficultyColor(
                  destination?.difficulty
                )} rounded-full text-white text-sm font-bold shadow-lg`}
              >
                {destination?.difficulty}
              </div>
              {destination?.max_elevation && (
                <div className="px-5 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Mountain className="w-4 h-4" />
                    {destination.max_elevation}
                  </div>
                </div>
              )}
            </div>

            {/* Scroll Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
              className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            >
              <div className="flex flex-col items-center gap-2">
                <span className="text-white/70 text-sm font-medium">Explore</span>
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
                >
                  <div className="w-1.5 h-1.5 bg-white rounded-full" />
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Image Gallery Carousel */}
      <div className="relative -mt-32 z-20 px-5 md:px-10 lg:px-16 xl:px-32">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-6xl mx-auto"
        >
          <Swiper
            effect="coverflow"
            grabCursor={true}
            centeredSlides={true}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            spaceBetween={30}
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 150,
              modifier: 1.5,
              slideShadows: true,
            }}
            loop={destinationImages?.results?.length > 3}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            navigation={true}
            modules={[EffectCoverflow, Autoplay, Navigation, Pagination]}
            className="destination-gallery-swiper"
          >
            {destinationImages?.results?.length > 0 ? (
              destinationImages.results.map((destinationImage, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="group cursor-pointer h-64 md:h-80 lg:h-96 rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 hover:shadow-mamba-sky-blue/30 hover:scale-105"
                    onClick={() => handleClick(destinationImage, index)}
                  >
                    <div className="relative w-full h-full">
                      <img
                        src={destinationImage.image_url || NoImage}
                        alt={`${destination?.name} view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {destinationImage.is_display_image && (
                        <div className="absolute top-4 right-4 px-3 py-1 bg-mamba-golden rounded-full">
                          <span className="text-xs font-bold text-mamba-navy">Featured</span>
                        </div>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))
            ) : (
              <SwiperSlide>
                <div className="h-64 md:h-80 lg:h-96 bg-mamba-bg-light rounded-2xl flex items-center justify-center">
                  <p className="text-mamba-navy/50 font-montserrat">No images available</p>
                </div>
              </SwiperSlide>
            )}
          </Swiper>
        </motion.div>
      </div>

      {/* Main Content Section */}
      <div className="px-5 md:px-10 lg:px-16 xl:px-32 py-20 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Left Section - Main Content */}
          <div className="lg:col-span-2">
            {destinationIsLoading || destinationIsFetching ? (
              <div className="flex justify-center py-20">
                <SpinningLoader isLoading={true} hasData={false} />
              </div>
            ) : (
              <div className="space-y-12">
                {/* Section Header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-1 w-12 bg-gradient-to-r from-mamba-sky-blue to-transparent rounded-full" />
                    <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-sky-blue">
                      About This Destination
                    </span>
                  </div>
                  <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black font-montserrat text-mamba-navy leading-tight">
                    Discover{" "}
                    <span className="bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                      {destination?.name}
                    </span>
                  </h2>
                </motion.div>

                {/* Description */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="prose prose-lg max-w-none font-inter text-slate-700 leading-relaxed"
                >
                  <HtmlContent htmlData={destination?.description} />
                </motion.div>

                {/* Trail Specifications */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-1 w-12 bg-gradient-to-r from-mamba-golden to-transparent rounded-full" />
                    <h3 className="text-2xl font-black font-montserrat text-mamba-navy">
                      Trail Specifications
                    </h3>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {destination?.max_elevation && (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="group relative overflow-hidden bg-gradient-to-br from-white to-mamba-bg-light border-2 border-mamba-sky-blue/20 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-mamba-sky-blue/5 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform" />
                        <div className="relative flex items-start gap-4">
                          <div className="p-3 bg-gradient-to-br from-mamba-sky-blue to-mamba-navy rounded-xl shadow-lg">
                            <Mountain className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-mamba-navy/70 mb-1">Max Elevation</p>
                            <p className="text-2xl font-black font-montserrat text-mamba-navy">
                              {destination.max_elevation}
                              {destination?.elevation_unit && (
                                <span className="text-sm font-semibold ml-1">{destination.elevation_unit}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {destination?.elevation_gain && (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="group relative overflow-hidden bg-gradient-to-br from-white to-mamba-bg-light border-2 border-mamba-golden/20 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-mamba-golden/5 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform" />
                        <div className="relative flex items-start gap-4">
                          <div className="p-3 bg-gradient-to-br from-mamba-golden to-amber-400 rounded-xl shadow-lg">
                            <TrendingUp className="w-6 h-6 text-mamba-navy" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-mamba-navy/70 mb-1">Elevation Gain</p>
                            <p className="text-2xl font-black font-montserrat text-mamba-navy">
                              {destination.elevation_gain}
                              {destination?.elevation_unit && (
                                <span className="text-sm font-semibold ml-1">{destination.elevation_unit}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {destination?.distance_in_km && (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="group relative overflow-hidden bg-gradient-to-br from-white to-mamba-bg-light border-2 border-mamba-sky-blue/20 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-mamba-sky-blue/5 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform" />
                        <div className="relative flex items-start gap-4">
                          <div className="p-3 bg-gradient-to-br from-mamba-sky-blue to-mamba-navy rounded-xl shadow-lg">
                            <NavigationIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-mamba-navy/70 mb-1">Distance</p>
                            <p className="text-2xl font-black font-montserrat text-mamba-navy">
                              {destination.distance_in_km} km
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {destination?.duration && (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="group relative overflow-hidden bg-gradient-to-br from-white to-mamba-bg-light border-2 border-mamba-golden/20 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-mamba-golden/5 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform" />
                        <div className="relative flex items-start gap-4">
                          <div className="p-3 bg-gradient-to-br from-mamba-golden to-amber-400 rounded-xl shadow-lg">
                            <Clock className="w-6 h-6 text-mamba-navy" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-mamba-navy/70 mb-1">Duration</p>
                            <p className="text-2xl font-black font-montserrat text-mamba-navy">
                              {destination.duration}
                              {destination?.duration_units && (
                                <span className="text-sm font-semibold ml-1">{destination.duration_units}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {destination?.location && (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="group relative overflow-hidden bg-gradient-to-br from-white to-mamba-bg-light border-2 border-mamba-sky-blue/20 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-mamba-sky-blue/5 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform" />
                        <div className="relative flex items-start gap-4">
                          <div className="p-3 bg-gradient-to-br from-mamba-sky-blue to-mamba-navy rounded-xl shadow-lg">
                            <MapPin className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-mamba-navy/70 mb-1">Location</p>
                            <p className="text-lg font-bold font-montserrat text-mamba-navy">
                              {destination.location}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {destination?.country && (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="group relative overflow-hidden bg-gradient-to-br from-white to-mamba-bg-light border-2 border-mamba-golden/20 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-mamba-golden/5 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform" />
                        <div className="relative flex items-start gap-4">
                          <div className="p-3 bg-gradient-to-br from-mamba-golden to-amber-400 rounded-xl shadow-lg">
                            <MapPin className="w-6 h-6 text-mamba-navy" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-mamba-navy/70 mb-1">Country</p>
                            <p className="text-lg font-bold font-montserrat text-mamba-navy">
                              {destination.country}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* Trail Features */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                    {destination?.is_child_friendly !== undefined && (
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="flex items-center gap-3 p-4 bg-white border-2 border-mamba-sky-blue/20 rounded-xl shadow-md"
                      >
                        <Baby className={`w-5 h-5 ${destination.is_child_friendly ? 'text-green-600' : 'text-slate-400'}`} />
                        <span className="text-sm font-bold text-mamba-navy">
                          {destination.is_child_friendly ? "Child Friendly" : "Not Child Friendly"}
                        </span>
                      </motion.div>
                    )}

                    {destination?.is_traverse !== undefined && (
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="flex items-center gap-3 p-4 bg-white border-2 border-mamba-golden/20 rounded-xl shadow-md"
                      >
                        <Route className={`w-5 h-5 ${destination.is_traverse ? 'text-mamba-golden' : 'text-slate-400'}`} />
                        <span className="text-sm font-bold text-mamba-navy">
                          {destination.is_traverse ? "Traverse Trail" : "Loop Trail"}
                        </span>
                      </motion.div>
                    )}

                    {destination?.type && (
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="flex items-center gap-3 p-4 bg-white border-2 border-mamba-sky-blue/20 rounded-xl shadow-md"
                      >
                        <Mountain className="w-5 h-5 text-mamba-sky-blue" />
                        <span className="text-sm font-bold text-mamba-navy">
                          {destination.type}
                        </span>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </div>
            )}
          </div>

          {/* Right Section - Sidebar */}
          <div className="lg:col-span-1 space-y-8">
            {/* Upcoming Events */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="sticky top-8 space-y-6"
            >
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-1 w-12 bg-gradient-to-r from-mamba-golden to-transparent rounded-full" />
                  <h3 className="text-2xl font-black font-montserrat text-mamba-navy">
                    Upcoming Events
                  </h3>
                </div>

                {adventuresIsLoading || adventuresIsFetching ? (
                  <div className="flex justify-center py-8">
                    <SpinningLoader isLoading={true} hasData={false} />
                  </div>
                ) : events.length > 0 ? (
                  <div className="space-y-6">
                    {events.slice(0, 2).map((event, index) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.15 }}
                        whileHover={{ y: -4 }}
                        onClick={() => navigate(`/events/${event?.slug}`)}
                        className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-mamba-sky-blue/20 hover:border-mamba-golden"
                      >
                        {/* Image */}
                        <div className="relative h-48 overflow-hidden">
                          <motion.img
                            whileHover={{ scale: 1.1 }}
                            transition={{ duration: 0.6 }}
                            src={
                              event?.destination?.images?.[0]?.image_url ||
                              event?.display_image_url ||
                              NoImage
                            }
                            alt={event.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/30 to-transparent" />

                          {/* Difficulty Badge */}
                          <div className="absolute top-3 right-3">
                            <div
                              className={`px-2.5 py-1 bg-gradient-to-r ${getDifficultyColor(
                                event?.destination?.difficulty
                              )} text-white text-xs font-bold rounded-full shadow-lg backdrop-blur-sm`}
                            >
                              {event?.destination?.difficulty || "Moderate"}
                            </div>
                          </div>

                          {/* Distance Badge */}
                          {event?.destination?.distance_in_km && (
                            <div className="absolute bottom-3 left-3">
                              <div className="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-slate-900 text-xs font-bold rounded-full">
                                {event?.destination?.distance_in_km} KM
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <div className="flex items-center gap-2 text-mamba-navy text-sm font-medium mb-2">
                            <MapPin className="w-4 h-4 text-mamba-sky-blue" />
                            <span className="truncate">
                              {event?.destination?.name || "Amazing Location"}
                            </span>
                          </div>
                          <h4 className="text-lg font-montserrat font-bold text-mamba-navy mb-3 group-hover:text-mamba-sky-blue transition-colors line-clamp-2">
                            {event?.name || event?.destination?.name}
                          </h4>

                          {/* Date & Details */}
                          <div className="flex items-center justify-between pt-3 border-t border-mamba-sky-blue/20">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Calendar className="w-4 h-4 text-mamba-sky-blue" />
                              <span className="font-medium">
                                {new Date(event.start_date).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                })}
                              </span>
                            </div>
                            <button className="text-mamba-sky-blue font-semibold group-hover:gap-2 flex items-center gap-1 transition-all text-sm">
                              Details
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Accent line at bottom */}
                        <div className="h-1 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                      </motion.div>
                    ))}

                    {events.length > 2 && (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigate('/events')}
                        className="w-full px-4 py-3 bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white rounded-xl font-bold font-montserrat hover:shadow-lg transition-all flex items-center justify-center gap-2"
                      >
                        View All Events
                        <ArrowRight className="w-5 h-5" />
                      </motion.button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-mamba-bg-light rounded-2xl border-2 border-mamba-sky-blue/20">
                    <div className="w-16 h-16 mx-auto mb-4 bg-mamba-sky-blue/10 rounded-full flex items-center justify-center">
                      <Calendar className="w-8 h-8 text-mamba-sky-blue/50" />
                    </div>
                    <p className="text-mamba-navy/60 font-medium">
                      No upcoming events for {destination?.name}
                    </p>
                  </div>
                )}
              </div>

              {/* Contact Card */}
              <div className="bg-gradient-to-br from-white to-mamba-bg-light border-2 border-mamba-sky-blue/20 rounded-3xl p-8 shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles className="w-6 h-6 text-mamba-golden" />
                  <h3 className="text-xl font-black font-montserrat text-mamba-navy">
                    Plan Your Trip
                  </h3>
                </div>
                <p className="text-sm text-slate-600 mb-6 font-inter">
                  Ready to explore {destination?.name}? Get in touch with us to plan your adventure.
                </p>
                <div className="space-y-3">
                  <a
                    href="tel:+254712345678"
                    className="flex items-center gap-3 p-4 bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white rounded-xl hover:shadow-lg transition-all group"
                  >
                    <Phone className="w-5 h-5" />
                    <span className="font-bold font-montserrat">Call Us</span>
                    <ArrowRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                  <a
                    href="https://wa.me/254712345678"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:shadow-lg transition-all group"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-bold font-montserrat">WhatsApp</span>
                    <ArrowRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                  <a
                    href="mailto:info@mambahikers.com"
                    className="flex items-center gap-3 p-4 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy rounded-xl hover:shadow-lg transition-all group"
                  >
                    <Mail className="w-5 h-5" />
                    <span className="font-bold font-montserrat">Email Us</span>
                    <ArrowRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative min-h-[50vh] bg-call-to-action2 bg-cover bg-center bg-fixed overflow-hidden"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/90 to-mamba-sky-blue/85" />

        {/* Animated Background Elements */}
        <div className="absolute top-20 left-20 w-64 h-64 bg-mamba-golden/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-mamba-sky-blue/10 rounded-full blur-3xl animate-pulse" />

        {/* Content */}
        <div className="relative z-10 flex items-center justify-center min-h-[50vh] px-5 md:px-10 lg:px-16 xl:px-32 py-20">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 mb-6 px-5 py-2 bg-mamba-golden/20 backdrop-blur-md border border-mamba-golden/40 rounded-full">
                <Sparkles className="w-5 h-5 text-mamba-golden" />
                <span className="text-sm text-white font-montserrat font-bold tracking-wider uppercase">
                  Private Adventures
                </span>
              </div>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black font-montserrat text-white mb-6 leading-tight">
                Create Your Own
                <span className="block bg-gradient-to-r from-mamba-golden to-amber-400 bg-clip-text text-transparent">
                  Custom Adventure
                </span>
              </h2>

              <p className="text-lg md:text-xl text-white/90 mb-10 max-w-3xl mx-auto font-inter leading-relaxed">
                Looking for a private hiking experience? We can organize a custom trip to{" "}
                <span className="font-bold text-mamba-golden">{destination?.name}</span> just for
                you and your group.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <motion.a
                  href="mailto:info@mambahikers.com"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group px-8 py-4 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-black font-montserrat rounded-xl shadow-lg hover:shadow-2xl transition-all flex items-center gap-2 uppercase tracking-wide"
                >
                  <Mail className="w-5 h-5" />
                  <span>Contact Us</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.a>

                <motion.a
                  href="https://wa.me/254712345678"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group px-8 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white font-bold font-montserrat rounded-xl hover:bg-white/20 transition-all flex items-center gap-2 uppercase tracking-wide"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </ClientLayout>
  );
};

export default DestinatioDetail;
